import React from 'react';
import { Alert, Box, Divider, Paper, Tooltip, Typography } from '@mui/material';
import StatusChip from '../../../../shared/components/StatusChip';
import StageStepper from '../../../../shared/components/StageStepper';
import { derivePhases, type PhaseKey } from './derivePhases';
import PlanPhase from './PlanPhase';
import ExecutePhase from './ExecutePhase';
import VerifyPhase from './VerifyPhase';
import PlumbingSection from './PlumbingSection';
import { formatDuration, relativeTime } from './utils';
import type { ReconcilerExecution } from '../../../../types/frontend';

export interface ExecutionCardProps {
  execution: ReconcilerExecution;
  variant?: 'outlined' | 'inline';
  /** Phases the reconciler declares it runs; threads into the stepper. */
  declaredPhases?: PhaseKey[];
  /** Decode the raw resource id into human label parts; defaults to a single raw part. */
  formatResourceParts?: (resourceId: string) => string[];
}

const ExecutionCard: React.FC<ExecutionCardProps> = ({
  execution,
  variant = 'outlined',
  declaredPhases,
  formatResourceParts = (id) => [id],
}) => {
  const rawResource = execution.resource_label || execution.resource;
  const resourceParts = formatResourceParts(rawResource);
  const maxMs = Math.max(
    execution.plan?.duration_ms ?? 0,
    execution.execute?.duration_ms ?? 0,
    execution.verify?.duration_ms ?? 0,
    1,
  );

  const isInline = variant === 'inline';

  const header = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        px: isInline ? 0 : 2,
        py: 1.5,
        ...(!isInline && { borderBottom: 1, borderColor: 'divider' }),
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <StatusChip status={execution.status} size="small" />
          <Typography variant="caption" color="text.disabled">
            {relativeTime(execution.started_at)}
          </Typography>
        </Box>
        <Tooltip title={rawResource} placement="top-start">
          <Box>
            {resourceParts.map((part, i) => (
              <Typography
                key={i}
                variant={i === 0 ? 'subtitle2' : 'caption'}
                sx={{
                  display: 'block',
                  fontWeight: i === 0 ? 600 : 400,
                  color: i === 0 ? 'text.primary' : 'text.secondary',
                  letterSpacing: i === 0 ? '-0.01em' : undefined,
                  lineHeight: 1.35,
                }}
              >
                {part}
              </Typography>
            ))}
          </Box>
        </Tooltip>
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.25 }}>
          {execution.reconciler_description || execution.reconciler}
        </Typography>
      </Box>

      <Box sx={{ textAlign: 'right', flexShrink: 0, ml: 2 }}>
        <Typography
          variant="h6"
          sx={{ fontWeight: 700, fontFamily: 'monospace', lineHeight: 1.2 }}
        >
          {formatDuration(execution.total_ms)}
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.6rem' }}
        >
          total
        </Typography>
      </Box>
    </Box>
  );

  const hasPhases = execution.plan || execution.execute || execution.verify;
  const hasCause = !hasPhases && execution.cause;

  const phases = (
    <Box sx={{ px: isInline ? 0 : 2, py: 0.5 }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', pb: 1, mb: 0.5 }}>
        <StageStepper
          steps={derivePhases(execution, declaredPhases)}
          ariaLabel="Reconciler pipeline phases"
        />
      </Box>

      {hasCause && (
        <Alert
          severity={execution.historyUnavailable ? 'info' : 'error'}
          variant="outlined"
          sx={{
            my: 1,
            py: 0.5,
            px: 1.5,
            '& .MuiAlert-icon': { fontSize: 16, mr: 1, py: 0.5 },
            '& .MuiAlert-message': { py: 0 },
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
            {execution.error ?? 'Execution failed'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {execution.cause}
          </Typography>
        </Alert>
      )}

      {execution.plan && <PlanPhase plan={execution.plan} maxMs={maxMs} />}

      {execution.execute && (
        <>
          <Divider />
          <ExecutePhase execute={execution.execute} maxMs={maxMs} />
        </>
      )}

      {execution.verify && (
        <>
          <Divider />
          <VerifyPhase verify={execution.verify} maxMs={maxMs} />
        </>
      )}

      {execution.plumbing.length > 0 && (
        <PlumbingSection plumbing={execution.plumbing} />
      )}
    </Box>
  );

  if (isInline) {
    return <Box>{header}{phases}</Box>;
  }

  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
      {header}
      {phases}
    </Paper>
  );
};

export default ExecutionCard;
